import type {
  BreakDecisionOption,
  BreakDecisionRiskLevel,
  CoverageGap,
  TesterDevice,
} from '@runhuman/shared';
import { formatBreakWindowInTz, formatLocalTime12 } from './break-request-format';

/**
 * Per-risk indicator emoji used in plain-text surfaces (Slack ops escalation,
 * markdown emails, anywhere CSS coloring isn't available). Single source of
 * truth so a future palette change touches one map.
 *
 * For DOM-rendered surfaces (admin decision modal alternatives, the
 * `BreakRequestRow` reasoning line, Counter-button badge), prefer
 * {@link riskGlyph} + {@link RISK_COLOR} — they render `⚠` via the unicode
 * text-presentation form so CSS `color` can recolor it red/yellow.
 */
export const RISK_ICON: Record<BreakDecisionRiskLevel, string> = {
  ok: '🟢',
  tight: '🟡',
  gap: '🔴',
};

/**
 * Text-presentation form of `U+26A0` (warning sign) — the trailing VS15
 * selector forces the glyph to render as a monochrome outlined character
 * instead of the OS's color emoji. That makes it recolorable via CSS
 * `color`, which the emoji form (`⚠️`) silently ignores.
 *
 * Used by `BreakRequestRow`'s Row 2 reasoning + Counter risk badge, and by
 * `BreakDecisionAlternativeRow`'s leading icon.
 */
export const WARNING_TEXT_GLYPH = '⚠︎';

/**
 * Text-presentation form of `U+2714` (heavy check mark) — companion to
 * {@link WARNING_TEXT_GLYPH} for the `ok` end of the risk scale. VS15
 * forces a monochrome glyph so CSS `color: green` actually takes effect;
 * the emoji form (`✅`, `U+2705`) is OS-painted and ignores CSS color.
 */
export const CHECK_TEXT_GLYPH = '✔︎';

/**
 * CSS color tokens for the text-presentation `⚠` glyph (and any other
 * inline risk indicator that needs to match the row's visual language).
 * Static hex values — the #4451 row redesign locked these colors and any
 * downstream theme override would dilute the at-a-glance scan.
 */
export const RISK_COLOR: Record<BreakDecisionRiskLevel, string> = {
  ok: '#10b981',
  tight: '#f59e0b',
  gap: '#ef4444',
};

/**
 * Pick the right text-presentation glyph for a risk level. `ok` uses
 * `✔` (heavy check, VS15), `tight` and `gap` use `⚠` (warning, VS15) —
 * both are monochrome and the caller is expected to wrap them in a span
 * with `style={{ color: RISK_COLOR[level] }}` to get the green / yellow /
 * red ink. Keeping the whole family on CSS-colored text glyphs (rather
 * than mixing `✅` emoji with `⚠` text) means the indicator weight is
 * visually consistent across the row, the modal, and any future surface.
 */
export function riskGlyph(level: BreakDecisionRiskLevel): string {
  return level === 'ok' ? CHECK_TEXT_GLYPH : WARNING_TEXT_GLYPH;
}

const DEVICE_LABEL: Record<TesterDevice, string> = {
  ios: 'iOS',
  android: 'Android',
  pc: 'PC',
  mac: 'Mac',
};

/**
 * Heading text for an option row — `"Originally requested · 9:00 AM–9:15 AM"`
 * or `"Alternative · 9:15 AM–9:30 AM (+15 min)"`. The risk indicator is
 * rendered separately by the caller (as a CSS-colorable JSX element via
 * {@link riskGlyph} + {@link RISK_COLOR}); this function intentionally
 * returns icon-free text so a renderer can place the icon wherever it wants.
 */
export function formatBreakOptionHeading(
  option: BreakDecisionOption,
  timezone: string,
): string {
  const window = formatBreakWindowInTz(option.startsAt, option.endsAt, timezone);
  return option.kind === 'requested'
    ? `Originally requested · ${window}`
    : `Alternative · ${window} (${signed(option.displacementMinutes)} min)`;
}

/** Short on-duty + devices-preserved line — `"2 testers on duty · Tester covers: iOS, Android"`. */
export function formatOptionDigestLine(option: BreakDecisionOption): string {
  const onDutyCount = option.digest.onDutyTesterCount;
  const onDuty = `${onDutyCount} tester${onDutyCount === 1 ? '' : 's'} on duty`;
  const devices = option.devicesPreserved.length > 0
    ? `Tester covers: ${option.devicesPreserved.map((d) => DEVICE_LABEL[d]).join(', ')}`
    : 'Tester covers: no required devices';
  return `${onDuty} · ${devices}`;
}

/**
 * Collapse a list of per-(device, hour) coverage gaps into compact human-
 * readable lines grouped by device + contiguous-hour range. One line per
 * device, e.g. `"⚠ iOS coverage drops to 1 from 9:00 AM–11:00 AM"`. Returns
 * an empty array when the option leaves no shortfall — the modal hides the
 * line entirely instead of rendering "no gaps" noise.
 *
 * Uses the text-presentation `⚠` glyph (matching `BreakRequestRow`'s Row 2
 * reasoning line) so the renderer can CSS-color it red. The leading glyph
 * is included in the string for layout simplicity; the caller styles the
 * whole line in `RISK_COLOR.gap` since every line here is a hard shortfall.
 */
export function formatCoverageGapLines(
  gaps: readonly CoverageGap[],
  timezone: string,
): readonly string[] {
  if (gaps.length === 0) return [];
  const byDevice = new Map<TesterDevice, CoverageGap[]>();
  for (const gap of gaps) {
    const list = byDevice.get(gap.device);
    if (list) {
      list.push(gap);
    } else {
      byDevice.set(gap.device, [gap]);
    }
  }
  const out: string[] = [];
  for (const [device, deviceGaps] of byDevice) {
    for (const range of buildRanges(deviceGaps)) {
      const start = formatLocalTime12(range.startIso, timezone);
      const end = formatLocalTime12(range.endIso, timezone);
      out.push(
        `${WARNING_TEXT_GLYPH} ${DEVICE_LABEL[device]} coverage drops to ${range.minProjected} from ${start}–${end}`,
      );
    }
  }
  return out;
}

interface GapRange {
  readonly startIso: string;
  readonly endIso: string;
  readonly minProjected: number;
}

const HOUR_MS = 60 * 60 * 1000;

function buildRanges(gaps: readonly CoverageGap[]): readonly GapRange[] {
  const sorted = [...gaps].sort((a, b) => a.hour.localeCompare(b.hour));
  const ranges: { startIso: string; endMs: number; minProjected: number }[] = [];
  for (const gap of sorted) {
    const startMs = Date.parse(gap.hour);
    const endMs = startMs + HOUR_MS;
    const last = ranges[ranges.length - 1];
    if (last && startMs === last.endMs) {
      last.endMs = endMs;
      last.minProjected = Math.min(last.minProjected, gap.projectedAvailable);
    } else {
      ranges.push({ startIso: gap.hour, endMs, minProjected: gap.projectedAvailable });
    }
  }
  return ranges.map((r) => ({
    startIso: r.startIso,
    endIso: new Date(r.endMs).toISOString(),
    minProjected: r.minProjected,
  }));
}

function signed(minutes: number): string {
  return minutes >= 0 ? `+${minutes}` : `${minutes}`;
}
